import SectionHeader from './SectionHeader'
import { useScrollAnimation } from '../hooks/useScrollAnimation'

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    features: [
      { text: '1 portfolio · up to 10 holdings', included: true },
      { text: 'Dividend calendar (basic)', included: true },
      { text: 'DRIP snowball projector', included: true },
      { text: 'Basic dashboard metrics', included: true },
      { text: 'Advanced income reports', included: false },
      { text: 'Smart alerts', included: false },
      { text: 'Brokerage sync', included: false },
      { text: 'Covered Call & CSP tracker', included: false },
    ],
    cta: 'Get Started Free',
    ctaStyle: 'secondary',
    popular: false,
  },
  {
    name: 'Premium',
    price: '$7.99',
    period: '/month',
    subtext: 'Billed monthly · cancel anytime',
    features: [
      { text: '30 portfolios · Unlimited holdings', included: true },
      { text: 'Full calendar + ex-date alerts', included: true },
      { text: 'Advanced income reports + CSV/PDF', included: true },
      { text: 'Smart alerts (cuts, yield, ex-dates)', included: true },
      { text: 'Brokerage auto-sync via SnapTrade', included: true },
      { text: 'Email digests + weekly summaries', included: true },
      { text: 'Priority email support', included: true },
    ],
    cta: 'Upgrade to Premium',
    ctaStyle: 'primary',
    popular: true,
  },
  {
    name: 'Pro',
    price: '$11.99',
    period: '/month',
    features: [
      { text: 'Unlimited portfolios', included: true },
      { text: 'Everything in Premium', included: true },
      { text: 'Covered Call & CSP tracker (wheel strategy)', included: true },
      { text: 'Log premiums, track P&L, monitor open positions', included: true },
      { text: 'Priority support', included: true },
      { text: 'Early access to new features', included: true },
      { text: 'Advanced options analytics', included: true },
    ],
    cta: 'Upgrade to Pro',
    ctaStyle: 'secondary',
    popular: false,
  },
]

export default function PricingSection() {
  const ref = useScrollAnimation({ childSelector: '.pricing-card', stagger: 0.15 })

  return (
    <section className="bg-white section-padding" id="pricing">
      <div className="content-max">
        <SectionHeader
          overline="Simple, honest pricing"
          headline="Start free. Upgrade when you're ready."
          subtext="No credit card required. Free plan forever."
        />

        <div ref={ref} className="mt-12 grid md:grid-cols-3 gap-6 items-start">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`pricing-card bg-white rounded-xl p-8 lg:p-10 relative ${
                plan.popular
                  ? 'border-2 border-[#1B5E20] shadow-card-hover md:-mt-4 md:mb-4'
                  : 'border border-border-custom hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-[#1B5E20] text-white text-xs font-medium px-4 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Plan Name */}
              <p className={`text-xs font-medium tracking-widest uppercase mb-4 ${plan.popular ? 'text-[#1B5E20]' : 'text-[#0F172A]'}`}>
                {plan.name}
              </p>

              {/* Price */}
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl lg:text-5xl font-display text-charcoal">{plan.price}</span>
                <span className="text-sm text-slate">{plan.period}</span>
              </div>

              {plan.subtext && (
                <p className="text-xs text-slate mb-6">{plan.subtext}</p>
              )}

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((f) => (
                  <li key={f.text} className="flex items-start gap-2.5">
                    {f.included ? (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="mt-0.5 shrink-0">
                        <path d="M3 8l3.5 3.5L13 4" stroke="#1B5E20" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="mt-0.5 shrink-0">
                        <path d="M4 4l8 8M12 4l-8 8" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
                      </svg>
                    )}
                    <span className={`text-sm ${f.included ? 'text-slate' : 'text-warm-grey line-through'}`}>{f.text}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <a
                href="/register"
                className={`block text-center font-medium text-sm py-3.5 rounded-full transition-all duration-400 ${
                  plan.ctaStyle === 'primary'
                    ? 'bg-[#1B5E20] text-white hover:bg-[#145218] hover:shadow-gold-glow hover:-translate-y-px'
                    : 'border border-[#E2E8F0] text-[#0F172A] hover:border-[#1B5E20] hover:text-[#1B5E20]'
                }`}
              >
                {plan.cta}
              </a>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-[#94A3B8] mt-6">
          Free plan available forever. Upgrade anytime. Cancel anytime.
        </p>
      </div>
    </section>
  )
}
